import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Camera, CheckCircle2, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api, { apiError, API } from "@/lib/api";
import { useLang } from "@/i18n";
import { CountryField } from "@/components/CountryField";
import OccupantFields from "@/components/reservation/OccupantFields";
import { TurnstileBox } from "@/components/TurnstileBox";

const BOOKER = ["first_name", "last_name", "email", "phone", "birth_date", "street", "house_number", "postal_code", "city", "country", "passport_number", "passport_country", "passport_place"];
const TYPES = { email: "email", phone: "tel", birth_date: "date" };
const EMPTY_OCC = { kind: "adult", first_name: "", last_name: "", birth_date: "", passport_number: "", passport_country: "", country: "", street: "", postal_code: "", city: "" };

function Shell({ camp, logo, children }) {
  return (
    <div className="min-h-screen bg-[#0b1623] text-white px-5 py-10">
      <div className="mx-auto w-full max-w-3xl">
        <div className="mb-8 flex items-center gap-3">
          {logo && <img src={logo} alt="" className="h-11 w-auto rounded" />}
          <span className="text-lg font-semibold tracking-tight">{camp}</span>
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-sm sm:p-9 [&_input]:bg-white [&_input]:text-slate-900 [&_select]:bg-white [&_select]:text-slate-900 [&_label]:text-white/70">{children}</div>
        <p className="mt-6 text-center text-[11px] text-white/30">CampAd Pro</p>
      </div>
    </div>
  );
}

export default function Gegevens() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const tenant = params.get("tenant");
  const { t, changeLang } = useLang();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [b, setB] = useState({});
  const [occ, setOcc] = useState([]);
  const [stay, setStay] = useState(null);
  const [miss, setMiss] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [pitch, setPitch] = useState("");
  const [pitchLocked, setPitchLocked] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [photoId, setPhotoId] = useState(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [payUrl, setPayUrl] = useState("");
  const [tsToken, setTsToken] = useState(null);

  useEffect(() => {
    api.get(`/public/complete/${token}`, { params: { tenant } })
      .then(({ data }) => { setInfo(data); setB({ language: data.lang, ...data.booker }); setOcc(data.occupants); setStay(data.stay); changeLang(data.lang, false); })
      .catch((e) => setErr(apiError(e.response?.data?.detail) || e.message));
  }, [token, tenant, changeLang]);

  const logo = info?.logo_file_id ? `${API}/storage/files/${info.logo_file_id}` : null;
  if (err) return <Shell camp="CampAd Pro"><p className="text-white/70" data-testid="complete-error">{err}</p></Shell>;
  if (!info) return <Shell camp="CampAd Pro"><Loader2 className="h-5 w-5 animate-spin" /></Shell>;
  const L = info.labels;
  if (done || info.definitive) {
    return (
      <Shell camp={info.camp_name} logo={logo}>
        <div className="flex items-start gap-3" data-testid="complete-done">
          <CheckCircle2 className="h-6 w-6 text-emerald-400 shrink-0" />
          <p>{done ? L.done : L.already}</p>
        </div>
        {payUrl && (
          <div className="mt-6 rounded-xl border border-white/10 bg-white/[0.04] p-4" data-testid="complete-pay-box">
            <p className="text-sm text-white/70">{L.pay_intro}</p>
            <a href={payUrl} data-testid="complete-pay-link"
              className="mt-3 inline-flex items-center gap-2 rounded-lg bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-400 transition-colors">
              {L.pay_now}
            </a>
          </div>
        )}
      </Shell>
    );
  }

  const confirmPitch = () => {
    if (!pitch) { toast.error(L.choose_pitch); return; }
    if (window.confirm(L.pitch_confirm.replace("{pitch}", pitch))) setPitchLocked(true);
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    setPhotoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/public/complete/${token}/photo`, fd, { params: { tenant }, headers: { "Content-Type": "multipart/form-data" } });
      setPhotoId(data.file_id);
      toast.success(L.photo_done);
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || e.message); }
    finally { setPhotoBusy(false); }
  };

  const submit = async () => {
    if (info.needs_pitch && (!pitch || !pitchLocked)) { toast.error(L.choose_pitch); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/public/complete/${token}`, { booker: b, occupants: occ, stay, pitch_label: info.needs_pitch ? pitch : undefined, photo_file_id: photoId, turnstile: tsToken }, { params: { tenant } });
      if (data?.pay_url) setPayUrl(data.pay_url);
      setDone(true);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.missing) setMiss(new Set(d.missing));
      toast.error(apiError(d) || e.message);
    } finally { setBusy(false); }
  };
  const cls = (k) => (miss.has(k) || (!b[k] && k !== "phone") ? "border-rose-400" : "");

  if (!confirmed) {
    return (
      <Shell camp={info.camp_name} logo={logo}>
        <h1 className="font-display text-2xl font-bold" data-testid="identity-title">{L.identity_q}</h1>
        <p className="mt-2 text-white/70">{L.identity_sub}</p>
        <div className="mt-5 rounded-xl border border-white/10 bg-white/[0.04] p-4" data-testid="identity-card">
          <p className="text-lg font-semibold">{b.first_name} {b.last_name}</p>
          <p className="text-sm text-white/60">{L.stay}: {info.pitch_label || "—"} · {info.arrival} → {info.departure}</p>
        </div>
        <Button className="mt-6 w-full sm:w-auto" size="lg" onClick={() => setConfirmed(true)} data-testid="identity-confirm">
          <CheckCircle2 className="h-4 w-4 mr-2" />{L.identity_yes}
        </Button>
      </Shell>
    );
  }

  return (
    <Shell camp={info.camp_name} logo={logo}>
      <h1 className="font-display text-2xl font-bold" data-testid="complete-title">{L.title}</h1>
      <p className="mt-1 text-sm text-white/60">{L.stay}: {info.pitch_label || "—"} · {info.arrival} → {info.departure} · {info.stay.adults}+{info.stay.children}</p>
      <p className="mt-1 text-xs text-white/50">{L.required_hint}</p>

      {info.needs_pitch && (
        <div className="mt-6" data-testid="complete-pitch-choice">
          <label className="space-y-1 text-xs">
            <span>{L.choose_pitch} *</span>
            <select value={pitch} disabled={pitchLocked} onChange={(e) => setPitch(e.target.value)} data-testid="complete-pitch-select"
              className={`block w-full rounded-md border px-3 py-2 text-sm ${!pitch ? "border-rose-400" : ""} ${pitchLocked ? "opacity-60" : ""}`}>
              <option value="">{L.choose_pitch_ph}</option>
              {(info.free_pitches || []).map((p) => (
                <option key={p.label} value={p.label}>{p.zone ? `${p.zone} · ` : ""}{p.label}</option>
              ))}
            </select>
          </label>
          {pitchLocked ? (
            <p className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-emerald-500/20 px-3 py-1 text-xs font-semibold text-emerald-300" data-testid="complete-pitch-locked">
              <CheckCircle2 className="h-4 w-4" />{L.pitch_locked}: {pitch}
            </p>
          ) : (
            <Button size="sm" variant="secondary" className="mt-2" onClick={confirmPitch} data-testid="complete-pitch-confirm">{L.confirm_pitch_btn}</Button>
          )}
        </div>
      )}

      <h2 className="mt-7 text-xs font-bold uppercase tracking-[0.16em] text-white/50">{L.booker}</h2>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        {BOOKER.map((k) => (
          <label key={k} className="space-y-1 text-xs">
            <span>{t(`bf_${k}`) !== `bf_${k}` ? t(`bf_${k}`) : t(k)} *</span>
            {k === "country" || k === "passport_country"
              ? <CountryField value={b[k] || ""} onChange={(e) => setB({ ...b, [k]: e.target.value })} className={cls(k)} testid={`complete-${k}`} />
              : <Input type={TYPES[k] || "text"} value={b[k] || ""} onChange={(e) => setB({ ...b, [k]: e.target.value })} className={cls(k)} data-testid={`complete-${k}`} />}
          </label>
        ))}
      </div>

      <div className="mt-7 flex items-center justify-between">
        <h2 className="text-xs font-bold uppercase tracking-[0.16em] text-white/50">{L.occupants} ({occ.length}/{Math.max(0, info.stay.adults + info.stay.children - 1)})</h2>
        <Button size="sm" variant="secondary" onClick={() => setOcc([...occ, { ...EMPTY_OCC }])} data-testid="complete-add-occupant"><Plus className="h-4 w-4 mr-1" />+</Button>
      </div>
      <div className="mt-3 space-y-3">
        {occ.map((o, i) => (
          <div key={i} className="rounded-xl border border-white/10 bg-white/[0.03] p-3 text-slate-900 [&_label]:text-white/70 [&_span]:text-white/80" data-testid={`complete-occupant-${i}`}>
            <div className="flex justify-end"><button type="button" onClick={() => setOcc(occ.filter((_, j) => j !== i))} className="text-rose-300 hover:text-rose-200" data-testid={`complete-occupant-remove-${i}`}><Trash2 className="h-4 w-4" /></button></div>
            <OccupantFields occupant={o} onChange={(n) => setOcc(occ.map((x, j) => (j === i ? n : x)))} arrival={info.arrival} departure={info.departure} highlightMissing testPrefix={`complete-occ-${i}`} />
          </div>
        ))}
      </div>

      <h2 className="mt-7 text-xs font-bold uppercase tracking-[0.16em] text-white/50">{L.power}</h2>
      <p className="mt-1 text-xs text-white/50">{L.power_q}</p>
      <div className="mt-3 flex flex-wrap gap-2" data-testid="complete-power">
        {[["", L.power_none], ["6", "6 Amp"], ["10", "10 Amp"]].map(([v, lbl]) => (
          <button key={v || "none"} type="button" onClick={() => setStay({ ...stay, power: v || null })} data-testid={`complete-power-${v || "none"}`}
            className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${(stay?.power || "") === v ? "border-emerald-400 bg-emerald-500/20 text-emerald-200" : "border-white/15 text-white/70 hover:border-white/40"}`}>
            {lbl}
          </button>
        ))}
      </div>

      <h2 className="mt-7 text-xs font-bold uppercase tracking-[0.16em] text-white/50">{L.photo}</h2>
      <p className="mt-1 text-xs text-white/50">{L.photo_hint}</p>
      <div className="mt-3 flex items-center gap-3" data-testid="complete-photo">
        <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg bg-white/10 px-4 py-2.5 text-sm font-semibold text-white hover:bg-white/15">
          {photoBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
          {L.photo_upload}
          <input type="file" accept="image/*,application/pdf" capture="environment" className="hidden" data-testid="complete-photo-input"
            onChange={(e) => uploadPhoto(e.target.files?.[0])} disabled={photoBusy} />
        </label>
        {photoId && <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-300" data-testid="complete-photo-done"><CheckCircle2 className="h-4 w-4" />{L.photo_done}</span>}
      </div>

      <TurnstileBox siteKey={info.turnstile_site_key} onToken={setTsToken} />

      <Button className="mt-8 w-full sm:w-auto" size="lg" onClick={submit} disabled={busy || (info.turnstile_site_key && !tsToken)} data-testid="complete-submit">
        {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}{L.submit}
      </Button>
    </Shell>
  );
}
